export interface FormatHub {
  slug: string;
  formatName: string;
  extensions: string[];
  title: string;
  description: string;
  h1: string;
  lead: string;
  typicalSize: string;
  comparisonTitle: string;
  comparisonNote: string;
  specs: { label: string; value: string }[];
  whyEmailFails: string;
  bestPractices: string[];
  faq: { q: string; a: string }[];
}

export const FORMAT_HUBS: FormatHub[] = [
  {
    slug: "send-mp4",
    formatName: "MP4 Video",
    extensions: [".mp4", ".m4v"],
    title: "Send Large MP4 Video Files Online (Free, Up to 3 GB, No Account)",
    description: "Send large MP4 video files online without quality loss or email limits. Encrypted in your browser, up to 3 GB free, self-destructing links. No sign-up.",
    h1: "Send Large MP4 Video Files Without Quality Loss",
    lead: "MP4 is the universal standard for video, but modern 1080p and 4K recordings quickly reach 500 MB to 3 GB — far beyond Gmail and Outlook's 25 MB attachment ceiling. LimeShare lets you transfer full-resolution MP4s with client-side zero-knowledge encryption.",
    typicalSize: "200 MB – 2.5 GB (10–30 mins of H.264 / H.265)",
    comparisonTitle: "MP4 Attachment Reality",
    comparisonNote: "Messengers like WhatsApp compress MP4 videos to 720p and discard high-framerate metadata. LimeShare sends the exact source bitstream byte-for-byte.",
    specs: [
      { label: "Supported Codecs", value: "H.264 (AVC), H.265 (HEVC), AV1, VP9" },
      { label: "MIME Types", value: "video/mp4, video/x-m4v" },
      { label: "Typical 1080p Bitrate", value: "8 – 15 Mbps (≈ 75 MB per minute)" },
      { label: "Typical 4K Bitrate", value: "35 – 85 Mbps (≈ 350 MB per minute)" },
      { label: "LimeShare Max Size", value: "3 GB per file (free), up to 50 GB (Pro)" },
    ],
    whyEmailFails: "Email gateways enforce a strict 20–25 MB total MIME message limit. Base64 encoding inflates binary files by 33 %, meaning an MP4 file larger than 18 MB will immediately bounce with an 'attachment size exceeded' error.",
    bestPractices: [
      "Keep H.264/H.265 video and audio streams uncompressed inside the MP4 container for perfect playback.",
      "Use 'Burn after first download' if sharing confidential draft edits or unreleased footage.",
      "Add an Argon2id password to prevent unauthorized viewing even if the link is forwarded.",
    ],
    faq: [
      {
        q: "Will LimeShare compress or re-encode my MP4 file?",
        a: "No. Unlike YouTube, WhatsApp, or Google Drive, LimeShare never touches or re-encodes your video. Your MP4 is encrypted client-side and delivered byte-for-byte in original bitstream quality.",
      },
      {
        q: "Can the recipient play the MP4 directly in their browser?",
        a: "Yes. LimeShare includes an in-memory zero-knowledge media preview player. Recipients can stream and preview MP4s under 256 MB directly in RAM before saving.",
      },
      {
        q: "How fast is the MP4 upload and download?",
        a: "Uploads stream in parallel 8–64 MB chunks directly to high-speed encrypted edge storage with zero egress limits, saturating your full broadband connection.",
      },
    ],
  },
  {
    slug: "send-mov",
    formatName: "MOV / ProRes Video",
    extensions: [".mov", ".qt"],
    title: "Send Large MOV & ProRes Files (iPhone & Mac, No Quality Loss)",
    description: "Send large QuickTime MOV and Apple ProRes video files from iPhone and Mac. Zero compression, browser AES-256 encryption, up to 3 GB free.",
    h1: "Send Large QuickTime MOV & Apple ProRes Files",
    lead: "Apple devices record high-bitrate QuickTime MOV and ProRes footage by default. A 60-second 4K ProRes clip can weigh over 5 GB. LimeShare provides an encrypted direct storage pipe so creators can transfer native MOV files without re-compression.",
    typicalSize: "1 GB – 5 GB+ (Apple ProRes / QuickTime 4K 60fps)",
    comparisonTitle: "Apple Mail Drop vs LimeShare",
    comparisonNote: "Mail Drop is slow, limited to iCloud accounts, and retains files for 30 days. LimeShare requires no account, transfers faster, and self-destructs on your schedule.",
    specs: [
      { label: "Supported Formats", value: "Apple ProRes 422/HQ/Proxy, QuickTime H.265/H.264" },
      { label: "MIME Type", value: "video/quicktime" },
      { label: "ProRes 4K 30fps Data Rate", value: "≈ 5.5 GB per minute" },
      { label: "iPhone Cinematic MOV", value: "≈ 400 MB per minute" },
      { label: "LimeShare Limit", value: "3 GB per file (free), up to 50 GB (Pro)" },
    ],
    whyEmailFails: "QuickTime MOV files carry uncompressed alpha channels or high-bitrate ProRes streams that exceed standard email attachment limits within 3 seconds of footage.",
    bestPractices: [
      "Send native MOV clips directly without exporting to MP4 if the recipient is an editor needing raw color space.",
      "Use Chrome or Edge on desktop to stream multi-gigabyte MOV files directly to the filesystem.",
      "Set an expiration of 24 hours to keep client review links tidy.",
    ],
    faq: [
      {
        q: "Can I send iPhone 4K ProRes video files with LimeShare?",
        a: "Yes. Select the MOV file directly from your Photos library or Files app on iOS. Files up to 3 GB transfer free with end-to-end encryption.",
      },
      {
        q: "Does QuickTime color metadata survive the transfer?",
        a: "Yes. Every single byte, color profile (Rec. 709, Rec. 2020, Apple Log), and audio track is preserved identically because files are encrypted as raw binary streams.",
      },
    ],
  },
  {
    slug: "send-zip",
    formatName: "ZIP & TAR Archives",
    extensions: [".zip", ".tar.gz", ".7z", ".rar"],
    title: "Send Large ZIP Files Online (Fast, Encrypted, Up to 3 GB Free)",
    description: "Transfer large ZIP, 7Z, and TAR.GZ archives online for free. Client-side AES-256 encryption, no file size traps, auto-purged links. No account required.",
    h1: "Send Large ZIP & Compressed Archives Online",
    lead: "ZIP archives group multiple documents, project assets, and folders into one file. When archives exceed 25 MB, email gateways block them as security risks. LimeShare encrypts and transfers archives up to 3 GB free without inspection.",
    typicalSize: "100 MB – 3 GB (Codebases, design bundles, datasets)",
    comparisonTitle: "ZIP Virus Filtering Issue",
    comparisonNote: "Corporate mail filters routinely reject password-protected or nested ZIP archives as potential malware. LimeShare's zero-knowledge pipeline bypasses corporate filter false-positives.",
    specs: [
      { label: "Archive Types", value: "ZIP, ZIP64, TAR, GZ, 7Z, RAR, BZ2" },
      { label: "MIME Types", value: "application/zip, application/x-7z-compressed" },
      { label: "Encryption", value: "AES-256-GCM authenticated container" },
      { label: "Integrity Verification", value: "Frame-level authentication tags" },
      { label: "LimeShare Max Size", value: "3 GB per file (free), up to 50 GB (Pro)" },
    ],
    whyEmailFails: "Email security scanners cannot inspect encrypted ZIP contents, so many corporate mail servers drop incoming ZIP files automatically or block attachments over 25 MB.",
    bestPractices: [
      "Use ZIP64 format when creating archives larger than 4 GB.",
      "LimeShare's 'Download all (ZIP)' feature can also bundle multiple uploaded files into one ZIP automatically for the recipient.",
      "Use 'Burn after first download' for single-use confidential code or document handoffs.",
    ],
    faq: [
      {
        q: "Can I send password-protected ZIP files?",
        a: "Yes. You can send pre-encrypted ZIPs, or let LimeShare encrypt regular ZIPs client-side with Argon2id password protection.",
      },
      {
        q: "What if the ZIP upload gets interrupted?",
        a: "LimeShare's IndexedDB resume engine saves your upload progress. Re-open the tab, pick the ZIP file again, and it will resume from the exact missing byte.",
      },
    ],
  },
  {
    slug: "send-psd",
    formatName: "Photoshop PSD & PSB",
    extensions: [".psd", ".psb"],
    title: "Send Large Photoshop (PSD & PSB) Files to Clients (Confidential & Fast)",
    description: "Send large Adobe Photoshop PSD and PSB files to clients and printers. End-to-end encrypted, full layer integrity, up to 3 GB free. No registration.",
    h1: "Send Large Photoshop (PSD & PSB) Files to Clients",
    lead: "Multi-layered Adobe Photoshop PSD files with smart objects and print-ready resolutions easily balloon to several gigabytes. LimeShare delivers raw PSDs to clients, art directors, and print shops with zero-knowledge encryption.",
    typicalSize: "300 MB – 2.8 GB (Print layouts, 300 DPI composite art)",
    comparisonTitle: "WeTransfer vs LimeShare for Designers",
    comparisonNote: "WeTransfer free limits file retention and shows large ads. LimeShare offers clean, ad-free download pages with client-side encryption protecting your design IP.",
    specs: [
      { label: "Formats Supported", value: "Photoshop Document (PSD), Large Document Format (PSB)" },
      { label: "MIME Type", value: "image/vnd.adobe.photoshop" },
      { label: "Color Depth Preserved", value: "8-bit, 16-bit, 32-bit CMYK & RGB" },
      { label: "Layer Preservation", value: "100% binary exact (never flattened)" },
      { label: "LimeShare Limit", value: "3 GB per file (free), up to 50 GB (Pro)" },
    ],
    whyEmailFails: "A standard print poster or web composite at 300 DPI with 20 layers exceeds 200 MB, causing instant rejection by client email servers.",
    bestPractices: [
      "Do not rasterize or flatten your layers to save space — send the full source PSD using LimeShare's 3 GB limit.",
      "Add an encrypted note specifying fonts used or art directions.",
      "Use the 'Sent from this browser' dashboard to see when your client has pulled the design.",
    ],
    faq: [
      {
        q: "Are Photoshop smart objects and linked assets preserved?",
        a: "Yes. Because LimeShare operates as a pure binary zero-knowledge pipe, your PSD or PSB file remains 100 % intact with all layers, paths, masks, and smart objects.",
      },
      {
        q: "Can my client download the PSD without an account?",
        a: "Yes. They just click the link, and the file decrypts directly in their browser.",
      },
    ],
  },
  {
    slug: "send-raw",
    formatName: "RAW Camera Photos",
    extensions: [".cr2", ".cr3", ".nef", ".arw", ".dng", ".raf"],
    title: "Send RAW Camera Photos (Canon, Sony, Nikon, DNG — Full Resolution)",
    description: "Transfer uncompressed RAW camera photos to retouchers and clients. Canon CR3, Sony ARW, Nikon NEF, DNG supported. Up to 3 GB free, zero-knowledge encrypted.",
    h1: "Send Uncompressed RAW Photos to Retouchers & Clients",
    lead: "Professional camera sensors produce 40–100 MB per RAW frame. A shoot with 50 selects is gigabytes of uncompressed sensor data. LimeShare lets photographers transfer pristine RAW files to retouchers with zero compression.",
    typicalSize: "40 MB – 120 MB per frame (Sony A7R, Canon R5, Nikon Z9)",
    comparisonTitle: "Cloud Compression Warning",
    comparisonNote: "Google Photos and cloud drives frequently alter EXIF metadata or generate lossy previews. LimeShare transfers original sensor RAW files with full EXIF data intact.",
    specs: [
      { label: "Formats Supported", value: "Sony .ARW, Canon .CR2/.CR3, Nikon .NEF, Adobe .DNG, Fuji .RAF" },
      { label: "Sensor Depth", value: "14-bit & 16-bit uncompressed sensor data" },
      { label: "Metadata Integrity", value: "100% EXIF, GPS, and lens profiles intact" },
      { label: "Batch Transfers", value: "Up to 3 files × 3 GB each (or zipped shoot)" },
      { label: "LimeShare Limit", value: "3 GB per file (free), up to 50 GB (Pro)" },
    ],
    whyEmailFails: "Sending just two uncompressed RAW photos exceeds the 25 MB email limit. Uploading to public cloud links risks copyright theft and metadata stripping.",
    bestPractices: [
      "Package batch shoot selects into a ZIP file or transfer top selects directly.",
      "Protect your pre-release commercial shoots with an Argon2id password.",
      "Use 'Burn after first download' when delivering exclusive client finals.",
    ],
    faq: [
      {
        q: "Does LimeShare strip EXIF or camera color profiles?",
        a: "No. Zero metadata is stripped or altered. Retouchers receive the exact sensor data recorded by your camera.",
      },
      {
        q: "What RAW formats work with LimeShare?",
        a: "All of them: Canon (CR2/CR3), Sony (ARW), Nikon (NEF), Fujifilm (RAF), Panasonic (RW2), Leica/Adobe (DNG), and Hasselblad.",
      },
    ],
  },
  {
    slug: "send-4k-video",
    formatName: "4K & 8K Video",
    extensions: [".mp4", ".mov", ".mkv", ".m4v"],
    title: "How to Send 4K Video Files Without Compression (Zero Loss)",
    description: "Learn how to send 4K and 8K ultra-HD video files without compression or quality loss. Encrypted in your browser, up to 3 GB free, streaming downloads.",
    h1: "Send 4K Ultra-HD Video Files Without Compression",
    lead: "4K video delivers breathtaking sharpness, but requires massive bitrates: 300 MB to 1 GB per minute of footage. Sending 4K video via cloud chat or email ruins color grading and destroys bitrates. LimeShare transfers raw 4K files with zero degradation.",
    typicalSize: "1 GB – 3 GB (3–10 mins of 4K 60fps / 10-bit HDR)",
    comparisonTitle: "YouTube / Vimeo vs LimeShare",
    comparisonNote: "Video platforms re-compress 4K video to low-bitrate VP9/AV1 codecs. LimeShare delivers the uncompressed master file directly to your editor or client.",
    specs: [
      { label: "Resolution", value: "3840×2160 (4K UHD), 4096×2160 (DCI 4K), 8K UHD" },
      { label: "Frame Rates", value: "24fps, 30fps, 60fps, 120fps high-speed" },
      { label: "Color Spaces", value: "10-bit 4:2:2, HDR10, HLG, Dolby Vision" },
      { label: "Transfer Speed", value: "Multi-chunk parallel encrypted streaming pipe" },
      { label: "LimeShare Limit", value: "3 GB per file (free), up to 50 GB (Pro)" },
    ],
    whyEmailFails: "A single 5-second 4K video clip recorded on an iPhone 15/16 Pro exceeds the 25 MB email attachment limit. Standard messaging apps downscale to 1080p or 720p.",
    bestPractices: [
      "Keep 10-bit color grading intact by avoiding cloud platforms that re-encode.",
      "Recipient can preview 4K clips directly in their browser before saving to disk.",
      "Send links with a 48-hour expiration to keep client revision cycles clean.",
    ],
    faq: [
      {
        q: "How can I send a 4K video from my phone to a computer?",
        a: "Open limeshare.org in Safari or Chrome on your phone, choose the 4K video, copy the link or display the QR code, and open it on your computer.",
      },
      {
        q: "Can I send 4K 60fps drone footage?",
        a: "Yes. Drone footage from DJI, Autel, and GoPro transfers at 100% original bitrate with zero frame drops.",
      },
    ],
  },
  {
    slug: "send-large-pdf",
    formatName: "Large PDF Documents",
    extensions: [".pdf"],
    title: "Send Large PDF Files Too Big for Email (Free & Private)",
    description: "Send large PDF documents, blueprints, and scanned portfolios that exceed email limits. Client-side AES-256 encryption, up to 3 GB free, self-destructing.",
    h1: "Send Large PDF Documents Too Big for Email",
    lead: "High-resolution architectural blueprints, scanned legal filings, and print portfolios easily exceed 50 MB, causing email bounces. LimeShare sends large PDFs with end-to-end encryption so confidential documents remain strictly private.",
    typicalSize: "30 MB – 500 MB (Scanned contracts, vector blueprints, annual reports)",
    comparisonTitle: "Confidentiality of Legal Documents",
    comparisonNote: "Sending confidential legal briefs or tax audits over standard email exposes them to plaintext interception. LimeShare's zero-knowledge links keep legal data airtight.",
    specs: [
      { label: "Format Standard", value: "PDF 1.4 – 2.0, PDF/A, PDF/X print standard" },
      { label: "Vector & Scans", value: "High-DPI 600+ DPI scans and CAD vector blueprints" },
      { label: "Form Fields & Signatures", value: "100% cryptographic preservation of digital signatures" },
      { label: "Security", value: "AES-256-GCM browser container + Argon2id password option" },
      { label: "LimeShare Limit", value: "3 GB per file (free), up to 50 GB (Pro)" },
    ],
    whyEmailFails: "Legal, architecture, and medical PDFs contain embedded high-DPI scans that push message sizes past mail server quotas.",
    bestPractices: [
      "Always add a password for sensitive legal, financial, or medical PDF documents.",
      "Enable 'Burn after first download' when sharing single-recipient sensitive contracts.",
      "Recipients can preview PDF text and vector pages directly in browser.",
    ],
    faq: [
      {
        q: "Can LimeShare read or index the text in my PDF?",
        a: "No. The PDF is encrypted locally on your device with AES-256 before any byte touches the network. We cannot OCR, index, or view your document.",
      },
      {
        q: "Are digital signatures in the PDF preserved?",
        a: "Yes. Digital PKI signatures, form fields, and cryptographic seals remain 100% valid because the file is transmitted byte-for-byte.",
      },
    ],
  },
  {
    slug: "send-wav-audio",
    formatName: "WAV & Lossless Audio",
    extensions: [".wav", ".flac", ".aiff", ".m4a"],
    title: "Send Large WAV & FLAC Audio Stems (Lossless, Up to 3 GB Free)",
    description: "Send large 24-bit WAV, FLAC, and multi-track audio stems to producers and mastering engineers. Zero loss, browser encrypted, up to 3 GB free.",
    h1: "Send Uncompressed WAV & FLAC Audio Stems",
    lead: "Music producers and mixing engineers need uncompressed 24-bit 96kHz audio files. A multi-track session of 20 stems weighs over 2 GB. LimeShare delivers lossless audio files with zero compression and zero egress fees.",
    typicalSize: "50 MB – 2.5 GB (24-bit / 32-bit float master WAVs, stems packages)",
    comparisonTitle: "Lossless Audio Streaming vs Messaging Apps",
    comparisonNote: "Messengers convert audio to 128 kbps lossy OPUS or MP3. LimeShare guarantees 100% bit-perfect audio delivery for mastering and studio work.",
    specs: [
      { label: "Formats Supported", value: "WAV (PCM), FLAC, AIFF, ALAC, Broadcast WAV (BWF)" },
      { label: "Sample Rates", value: "44.1 kHz, 48 kHz, 96 kHz, 192 kHz, 32-bit float" },
      { label: "Channel Layouts", value: "Mono, Stereo, 5.1 Surround, Dolby Atmos ADM" },
      { label: "Browser Preview", value: "Built-in lossless in-memory audio player" },
      { label: "LimeShare Limit", value: "3 GB per file (free), up to 50 GB (Pro)" },
    ],
    whyEmailFails: "A single 4-minute 24-bit 96kHz stereo WAV file is approximately 80 MB — over three times the maximum email attachment size.",
    bestPractices: [
      "Bundle your multi-track stems into a ZIP or upload up to 3 full album master files.",
      "Recipients can listen directly in the browser using the preview player before downloading.",
      "Send rehearsal and unreleased demos with an Argon2id password to protect your copyright.",
    ],
    faq: [
      {
        q: "Does LimeShare re-sample or compress audio files?",
        a: "Never. The bitstream is encrypted raw and delivered bit-for-bit identical to the studio master.",
      },
      {
        q: "Can my client listen to the WAV file on their phone before downloading?",
        a: "Yes. LimeShare's preview player plays WAV and FLAC audio directly in mobile and desktop browsers.",
      },
    ],
  },
  {
    slug: "send-cad-dwg",
    formatName: "AutoCAD DWG & DXF",
    extensions: [".dwg", ".dxf", ".rvt", ".ifc"],
    title: "Send Large AutoCAD DWG, DXF & Revit BIM Files (Up to 3 GB Free)",
    description: "Send large architectural blueprints, AutoCAD DWG/DXF drawings, and Revit BIM models securely. End-to-end encrypted, zero accounts, up to 3 GB free.",
    h1: "Send Large AutoCAD DWG & Revit BIM Files Securely",
    lead: "Architectural blueprints, civil engineering site plans, and multi-discipline 3D BIM models routinely reach 200 MB to 2 GB. Email servers reject them, while public cloud links expose proprietary structural drawings. LimeShare delivers encrypted CAD files with zero data leakage.",
    typicalSize: "150 MB – 2.5 GB (AutoCAD project sheets, Revit central models)",
    comparisonTitle: "Autodesk Construction Cloud vs LimeShare",
    comparisonNote: "Autodesk Cloud requires expensive enterprise licenses and user accounts for outside subcontractors. LimeShare lets you send drawings to any fabricator or surveyor with an instant zero-knowledge link.",
    specs: [
      { label: "Supported CAD Formats", value: "AutoCAD .DWG/.DXF, Revit .RVT, Industry Foundation Classes .IFC, MicroStation .DGN" },
      { label: "Vector Geometry Preservation", value: "100% precision (never downsampled or rasterized)" },
      { label: "Security", value: "Client-side AES-256-GCM + Argon2id password option" },
      { label: "LimeShare Limit", value: "3 GB per file (free), up to 50 GB (Pro)" },
    ],
    whyEmailFails: "Complex drawings with external XREFs, point clouds, and 3D elevation geometry exceed mail server limits within a single sheet set.",
    bestPractices: [
      "Package your main DWG and external XREF dependencies into a single ZIP file or send as a multi-file batch.",
      "Protect sensitive building designs and government project specs with an Argon2id password.",
      "Check 'Burn after first download' when distributing bid tenders to contractors.",
    ],
    faq: [
      {
        q: "Are XREFs and external drawing links preserved?",
        a: "Yes. Binary files are transmitted untouched. When your recipient opens the drawing in AutoCAD, all internal block references and coordinate systems remain identical.",
      },
    ],
  },
  {
    slug: "send-iso-dmg",
    formatName: "ISO & DMG Disk Images",
    extensions: [".iso", ".dmg", ".img", ".vmdk"],
    title: "Send Large ISO & DMG Disk Images Online (Free, Up to 3 GB)",
    description: "How to send bootable ISO files, Apple DMG installers, and virtual machine disks online. Fast parallel encrypted streaming, end-to-end encrypted.",
    h1: "Send Large ISO & Apple DMG Disk Images Online",
    lead: "Operating system installers, bootable rescue media, and virtual machine disks (VMDK, VHDX) weigh between 1 GB and 5 GB. LimeShare's high-speed multi-part pipeline streams disk images directly to encrypted edge storage without bandwidth caps.",
    typicalSize: "1 GB – 3 GB+ (Linux distributions, macOS installers, VM snapshots)",
    comparisonTitle: "SFTP / FTP vs LimeShare",
    comparisonNote: "Setting up private SFTP servers requires port forwarding, firewall rules, and user management. LimeShare provides a zero-setup encrypted link with full gigabit transfer speeds.",
    specs: [
      { label: "Formats Supported", value: "ISO 9660, Apple Disk Image (DMG), Raw IMG, VMware VMDK, VirtualBox VDI" },
      { label: "Checksum Integrity", value: "Stream-level GCM authentication prevents bit flips" },
      { label: "Download Speed", value: "Unthrottled parallel chunk downloads" },
      { label: "LimeShare Limit", value: "3 GB per file (free), up to 50 GB (Pro)" },
    ],
    whyEmailFails: "Email gateways universally reject ISO files due to anti-virus heuristics, and sizes are 100x above email limits.",
    bestPractices: [
      "Use LimeShare's built-in SHA-256 checksum verifier (/tools/file-checksum) to verify the image hash after download.",
      "Chromium browsers (Chrome, Edge) stream the multi-gigabyte ISO directly to disk using the File System Access API.",
    ],
    faq: [
      {
        q: "Will the ISO boot properly after downloading?",
        a: "Yes. Every byte is verified with cryptographic authentication tags during decryption, guaranteeing zero corruption.",
      },
    ],
  },
  {
    slug: "send-3d-models",
    formatName: "3D Models & Blender Assets",
    extensions: [".blend", ".fbx", ".obj", ".gltf", ".glb", ".c4d", ".max"],
    title: "Send Large 3D Models & Blender Files (FBX, OBJ, GLTF, C4D)",
    description: "Transfer large 3D scene files, Blender projects, FBX character rigs, and 8K texture packs to artists and render farms. Free up to 3 GB.",
    h1: "Transfer Large 3D Models & Blender Scenes Securely",
    lead: "Modern 3D art workflows involve complex polygon meshes, character skinning weights, and 8K PBR texture maps (Albedo, Normal, Roughness, Displacement). Project folders routinely reach 2 GB to 5 GB. LimeShare gives 3D artists an encrypted direct transfer pipe.",
    typicalSize: "400 MB – 2.8 GB (Blender assets, Unreal Engine packages)",
    comparisonTitle: "Google Drive Quotas for 3D Artists",
    comparisonNote: "Sharing gigabytes of 3D assets on Google Drive quickly consumes free 15 GB account limits. LimeShare transfers up to 3 GB per batch with zero cloud quota consumption.",
    specs: [
      { label: "3D Formats Supported", value: "Blender (.blend), Autodesk (.fbx, .obj), Cinema 4D (.c4d), 3ds Max (.max), GLTF/GLB" },
      { label: "Texture Preservation", value: "100% binary fidelity for 16-bit/32-bit EXR & HDR textures" },
      { label: "Resume Engine", value: "Resumes automatically after Wi-Fi drops" },
      { label: "LimeShare Limit", value: "3 GB per file (free), up to 50 GB (Pro)" },
    ],
    whyEmailFails: "A single 4K texture set with 6 maps is 150 MB, causing immediate email rejection.",
    bestPractices: [
      "Pack external textures inside your .blend file before sending (File > External Data > Pack Resources).",
      "Use LimeDrop for instantaneous zero-cloud transfer if sending between workstations in the same studio.",
    ],
    faq: [
      {
        q: "Can I send complete Unreal Engine or Unity asset packages?",
        a: "Yes. Archive your asset folder into a ZIP file and send it up to 3 GB free on LimeShare.",
      },
    ],
  },
  {
    slug: "send-adobe-illustrator-ai",
    formatName: "Adobe Illustrator AI & EPS",
    extensions: [".ai", ".eps", ".svg"],
    title: "Send Large Adobe Illustrator AI & Vector Files to Printers",
    description: "Send large Adobe Illustrator AI and EPS vector files with embedded raster links to print shops and clients. Up to 3 GB free, zero-knowledge.",
    h1: "Send Large Adobe Illustrator (AI & EPS) Vector Files",
    lead: "Packaging designs, exhibition banners, and vehicle wraps created in Adobe Illustrator contain embedded high-res imagery that inflates vector AI files to hundreds of megabytes. LimeShare delivers print-ready vector artwork to printers without color profile alteration.",
    typicalSize: "100 MB – 1.5 GB (Print-ready vector packaging with embedded links)",
    comparisonTitle: "WeTransfer Ads vs LimeShare",
    comparisonNote: "WeTransfer shows third-party banner ads to your commercial clients. LimeShare offers clean, professional, distraction-free download pages.",
    specs: [
      { label: "Vector Formats Supported", value: "Adobe Illustrator (.ai), Encapsulated PostScript (.eps), Scalable Vector Graphics (.svg)" },
      { label: "Color Profile Fidelity", value: "CMYK (FOGRA, SWOP, GRACoL), Pantone spot colors 100% intact" },
      { label: "Font & Path Preservation", value: "Preserves outlined vectors and live text without flattening" },
      { label: "LimeShare Limit", value: "3 GB per file (free), up to 50 GB (Pro)" },
    ],
    whyEmailFails: "Vector graphics containing embedded 300 DPI product packaging imagery surpass email limits within seconds.",
    bestPractices: [
      "Include linked assets and font instructions in the optional encrypted message field.",
      "Protect pre-release packaging and re-branding assets with an Argon2id password.",
    ],
    faq: [
      {
        q: "Will my Pantone spot color channels be preserved?",
        a: "Yes. Files are transmitted bit-for-bit identical with no conversion or re-processing.",
      },
    ],
  },
];

export function getFormatHub(slug: string): FormatHub | undefined {
  return FORMAT_HUBS.find((f) => f.slug === slug);
}
